import dayjs from "dayjs";

/**
 * Stats response core — the shape shared by the two statistics endpoints
 * (`/statsapp/overview` and `/statsapp/club`). The admin response types
 * (`OverviewResponse`, `ClubResponse`) structurally satisfy this; each adds its
 * own subject-specific fields (clubs / addons / checkins) that the detail views
 * consume directly and this transform ignores.
 */
export type StatsSeries = {
  active: number;
  activations: number;
  terminations: number;
  terminationsInTestPhase: number;
  total: number;
  storno: number;
};

type Demographic = { count: number; averageAge: number };

export type StatsResponseCore = {
  total: {
    active: number;
    cancellations: number;
    terminations: number;
    terminationsInTestPhase: number;
    activations: number;
    stornos: number;
  };
  change: {
    total: string;
    terminations: string;
    terminationsInTestPhase: string;
    cancellations: string;
    activations: string;
  };
  demographics: {
    male: Demographic;
    female: Demographic;
    diverse: Demographic;
  };
  labels: string[];
  series: StatsSeries[];
  compare: {
    labels: string[];
    series: StatsSeries[];
  };
};

export type StatsViewModelOptions = {
  start: string;
  end: string;
  interval: string;
  compareWithPrevious: boolean;
};

export type CurrentPoint = { x: string; y: number; rest: StatsSeries };
export type ComparePoint = { x: string; y: number };

export type StatsViewModel = {
  chartData: {
    labels: string[];
    current: CurrentPoint[];
    compare: ComparePoint[] | null;
  };
  banner: "empty" | "stale" | "none";
  staleUntil: string | null;
  visibleRange: {
    current: [string, string];
    compare: [string, string];
  };
};

/**
 * dayjs label format per interval. `year` is `MMM` (unified — the two detail
 * views had drifted to `MMM` vs `MMM.`).
 */
const LABEL_FORMATS: Record<string, string> = {
  custom: "DD.MM.YY",
  month: "DD.MM.YY",
  year: "MMM",
  week: "dd",
};

/**
 * Project a {@link StatsResponseCore} plus the selected date range into a
 * presentation-ready {@link StatsViewModel}. Pure — no React, no i18n, no
 * Chart.js styling. Tolerates `undefined` (SWR's first load with
 * `keepPreviousData`) and always returns a well-formed view model; `banner`
 * stays `"none"` until real data arrives so first load never flashes "empty".
 */
export function statsViewModel(
  core: StatsResponseCore | undefined,
  { start, end, interval, compareWithPrevious }: StatsViewModelOptions
): StatsViewModel {
  const fmt = LABEL_FORMATS[interval] ?? "DD.MM.YY";
  const format = (label: string) => dayjs(label).format(fmt);

  const labels = core?.labels ?? [];
  const series = core?.series ?? [];

  const current: CurrentPoint[] = series.map((s, i) => ({
    x: format(labels[i]),
    y: s.active,
    rest: s,
  }));

  // Comparison is meaningless for a custom range (there is no "previous year"
  // window), so gate it out — the unified behaviour of the two detail views.
  const showCompare = compareWithPrevious && interval !== "custom";
  const compare: ComparePoint[] | null =
    showCompare && core
      ? // Slice to the current window so a longer compare series cannot push
        // misaligned points past the current range.
        core.compare.series.slice(0, series.length).map((s, i) => ({
          x: format(labels[i]),
          y: s.active,
        }))
      : null;

  let banner: StatsViewModel["banner"] = "none";
  let staleUntil: string | null = null;
  if (core) {
    if (labels.length === 0) {
      banner = "empty";
    } else {
      const lastLabel = labels[labels.length - 1];
      if (dayjs(end).isAfter(dayjs(lastLabel), "day")) {
        banner = "stale";
        staleUntil = lastLabel;
      }
    }
  }

  const compareLabels = core?.compare?.labels ?? [];
  const visibleRange: StatsViewModel["visibleRange"] = {
    current:
      labels.length === 0
        ? [start, end]
        : [labels[0], labels[labels.length - 1]],
    compare: [compareLabels[0], compareLabels[compareLabels.length - 1]],
  };

  return {
    chartData: { labels: labels.map(format), current, compare },
    banner,
    staleUntil,
    visibleRange,
  };
}
